import React from "react";

import { QualioQFrontEndProvider } from "@qualio/ui-components";
import type { CurrentUser as QCurrentUser } from "@qualio/ui-components/lib/types/CurrentUser";
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import ReactDOM from "react-dom";
import { QueryClient, QueryClientProvider } from "react-query";

import { App } from "./App";
import { QViewsStylingFix } from "./components/QViewsStylingFix/QViewsStylingFix";
import { setDesignControlsBaseURL } from "./api/apiConfig";
import { FeatureFlags } from "@design-controls/types";
import "./i18n/config";

(window as any).renderDesignControls = async (
  containerId: string,
  currentUser: QCurrentUser,
  analytics: SegmentAnalytics.AnalyticsJS,
) => {
  // Set base URL based on feature flag from currentUser object
  const ldFeatureFlags =
    (currentUser.company as any)?.ld_feature_flags ||
    (currentUser as any).ld_feature_flags;
  const useV2 = ldFeatureFlags?.[FeatureFlags.API_V2] === true;
  const baseURL =
    useV2 && process.env.REACT_APP_API_BASE_URL_V2
      ? process.env.REACT_APP_API_BASE_URL_V2
      : process.env.REACT_APP_API_BASE_URL;
  setDesignControlsBaseURL(baseURL || "");

  const LDProvider = await asyncWithLDProvider({
    clientSideID: process.env.REACT_APP_LAUNCHDARKLY_PUBLIC_KEY as string,
    user: {
      key: `${currentUser?.companyId}`,
      custom: {
        loggedInUserId: `${currentUser?.userId}`,
        createdTime: `${currentUser.company?.createdTime}`,
        status: `${currentUser.company?.status}`,
      },
    },
    options: {},
  });

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  });

  ReactDOM.render(
    <QueryClientProvider client={queryClient}>
      <LDProvider>
        <QViewsStylingFix>
          <QualioQFrontEndProvider user={currentUser} analytics={analytics}>
            <App />
          </QualioQFrontEndProvider>
        </QViewsStylingFix>
      </LDProvider>
    </QueryClientProvider>,
    document.getElementById(containerId),
  );
};

(window as any).unmountDesignControls = (containerId: string) => {
  try {
    console.log("attempting to unmount container", containerId);
    const container = document.getElementById(containerId);

    if (container !== null) {
      ReactDOM.unmountComponentAtNode(container);
    }
  } catch (e) {
    console.error("error unmounting design controls container", e);
  }
};
